
import { JSONParse, Plan, Room, CameraData, Captured, Wall } from '../jsonParse';
import { ThreeView } from '../../view3d/webcad/View3dWebCad';
import * as THREE from 'three';
import { WallToPointList, CreateObject, CreatePointData, AddWall2D, AddRoom2D, AddFitting2D, AddFurniture2D, decideCeilingHeight, CorrectionManager, FurnitureInfo, Draw3d, AddPin2D, PinInfo} from './index';
import * as marietta from '../..';
import { PointInfo, WalltoFittingInfo, WallType } from '../WalltoFittingInfo';
import { BUILDING_TYPE_MANSION, BUILDING_TYPE_HOUSE } from '../../webcad/Constants';
import { CorrectionType } from './modifier/index';
import { FurnitureType } from './FurnitureInfo';
import { PinType } from './PinInfo';

export class LoadJSON {

    public pointList: PointInfo[][];

    public roomList: number[][][];

    private firstFloor: THREE.Group;

    private secondFloor: THREE.Group;

    private thirdFloor: THREE.Group;

    private viewOffsetFirst: THREE.Matrix4;

    private viewOffsetSecond: THREE.Matrix4;

    private viewOffsetThird: THREE.Matrix4;

    private plan: Plan;

    private room: Room;

    private offset;

    private captured: Captured[];

    public windows = [];

    public doors = [];

    public walls: Wall[][] = [];

    public openings = [];

    public objects = [];
    
    private version = [];

    private buildingType: number;

    private cameraData: CameraData[][] = [];

    private wallToFittingInfo: WallType[];

    public furnitureInfo: FurnitureType[][];

    public pinInfo: PinType[][];

    private furnitureFloor1: THREE.Group;

    private floorHeight: number[]; // 各階のfl高さが0のときの座標値

    // 複数階来た時
    constructor(jsonParse: JSONParse) {
        this.parse(jsonParse);
        ThreeView.get().correctionManager = new CorrectionManager(this.buildingType);

        this.pointList = [];
        this.roomList = []
        this.wallToFittingInfo = [];
        this.floorHeight = [];
        
        this.firstFloor = new THREE.Group();
        this.secondFloor = new THREE.Group();
        this.thirdFloor = new THREE.Group();

        this.furnitureFloor1 = new THREE.Group();

        // 家の形・offset値をセット
        this.setHouseData();

        // 3D表示確認用
        // this.create3D();    
    }

    /**
     * JSONデータをパースする
     * */
    private parse(jsonParse: JSONParse) {
        this.plan = jsonParse.plan;
        this.buildingType = this.plan.buildingType;
        this.room = this.plan.room;
        this.offset = this.room.offset ? this.room.offset : this.room.after;
        if (this.room.cameraData) this.cameraData = this.room.cameraData;
        this.captured = this.room.captured;

        // (i = 0) => 1階　(i = 1) => 2階　(i = 2) => 3階
        for (let floor = 0; floor < this.captured.length; floor++) {
            if (this.captured[floor] == null) continue;
            this.windows.push(this.captured[floor].windows);
            this.doors.push(this.captured[floor].doors);
            this.walls.push(this.captured[floor].walls);
            this.openings.push(this.captured[floor].openings);
            this.objects.push(this.captured[floor].objects);
            this.version.push(this.captured[floor].version);
        }
    }

    /** 戸建て・マンションモード切替 */
    private modeChange() {
        /** マンションモードでない時はマンションモードに切り替える */
        if (this.buildingType == BUILDING_TYPE_MANSION) {
            marietta.webcad.WebCADGlobal.webcad2Dmanager.buildingType = BUILDING_TYPE_MANSION;
            (<HTMLInputElement>document.getElementById('btnRoomDragInput')).checked = false;
            (<HTMLInputElement>document.getElementById('btnRoomKakomiInput')).checked = true;
            marietta.webcad.WebCADGlobal.changeFloor(1);
        }

        /** 戸建てモードでない時は戸建てモードに切り替える */
        if (this.buildingType == BUILDING_TYPE_HOUSE) {
            marietta.webcad.WebCADGlobal.webcad2Dmanager.buildingType = BUILDING_TYPE_HOUSE;
        }
        
        // アイテムメニュー初期表示
        marietta.webcad.WebCADGlobal.itemMenu.initView();
    }

    /**
     * JSON読込(壁取得・部屋取得)とデータ構築
     * */
    public execute(): void {
        marietta.webcad.WebCADGlobal.layerGroup.initMe(); // 図面からアイテムを削除
        
        const offset = [this.viewOffsetFirst, this.viewOffsetSecond, this.viewOffsetThird];
        
        /** 戸建て・マンションモード切替 */
        this.modeChange();

        for (let floor = 0; floor < this.walls.length; floor++) {
            /** 壁の頂点リスト作成 */
            this.pointList[floor] = new WallToPointList(this.walls[floor], 0, 0, false).execute();

            this.floorHeight[floor] = this.getFloorHeight(this.walls[floor]);
        }

        /** 壁と建具のデータ構築 */
        this.wallToFittingInfo = new WalltoFittingInfo(this.pointList, this.walls, this.windows, this.doors, this.openings).execute();

        for (let floor = 0; floor < this.walls.length; floor++) {
            /** JSONに出力されたoffset値を適用して、頂点リストを取得 */
            this.pointList[floor] = this.applyOffset(this.pointList[floor], offset[floor]);
        }

        /** 天井高設定 */
        new decideCeilingHeight(this.buildingType, this.walls, 2500).execute();

        /** 住設のデータ構築 */
        this.furnitureInfo = new FurnitureInfo(this.objects).execute();
        /** アプリのオフセットがある場合はオフセットをかける */
        this.furnitureInfo = this.offsetFurniture(this.furnitureInfo, offset);

        /** ピンデータ構築 */
        this.pinInfo = new PinInfo(this.cameraData, this.floorHeight).execute();
        /** アプリのオフセットがある場合はオフセットをかける */
        this.pinInfo = this.offsetPin(this.pinInfo, offset);

        /** 頂点リストから部屋・壁を２Ｄ描画 */
        for (let floor = 0; floor < this.walls.length; floor++) {

            this.DrawFromPointList(this.pointList[floor], floor+1);       

        }

        marietta.WebCADGlobal.itemManager.currentItem = null;
    }    

    /** アプリのオフセットがある場合はオフセットをかける */
    private offsetFurniture(furniture: FurnitureType[][], offset: THREE.Matrix4[]): FurnitureType[][] {
        if (offset.length == 0) return furniture;
        for (let floor = 0; floor < offset.length; floor++) {
            if (offset[floor] == null) break;
            if (furniture[floor].length == 0) continue;
            const quaternion = new THREE.Quaternion();
            offset[floor].decompose(new THREE.Vector3(), quaternion, new THREE.Vector3());
            const vec3 = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion);
            const rotation = vec3.angleTo(new THREE.Vector3(1, 0, 0));
            const sin = vec3.clone().normalize().clone().cross(new THREE.Vector3(1, 0, 0));
            const deg = sin.y > 0 ? THREE.MathUtils.radToDeg(rotation) : - THREE.MathUtils.radToDeg(rotation);
            for (let i = 0; i < furniture[floor].length; i++) {
                furniture[floor][i].position = furniture[floor][i].position.applyMatrix4(offset[floor]);
                furniture[floor][i].rotation = furniture[floor][i].rotation + deg;
            }
        }
        return furniture;
    }

    /** アプリのオフセットがある場合はオフセットをかける */
    private offsetPin(pin: PinType[][], offset: THREE.Matrix4[]): PinType[][] {
        if (offset.length == 0) return pin;
        
        for (let floor = 0; floor < offset.length; floor++) {
            if (offset[floor] == null) break;
            if (pin == null || pin[floor] == null || pin[floor].length == 0) continue;
            for (let i = 0; i < pin[floor].length; i++) {
                pin[floor][i].position.applyMatrix4(offset[floor]);
                const position = new THREE.Vector3();
                const quaternion = new THREE.Quaternion();
                offset[floor].decompose(position, quaternion, new THREE.Vector3());
                const vec3 = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion);
                let angle = vec3.angleTo(new THREE.Vector3(1, 0, 0));
                const cross = vec3.clone().cross(new THREE.Vector3(1, 0, 0));
                const rotation = cross.y < 0 ? angle : -angle;
                pin[floor][i].dir = pin[floor][i].dir.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), rotation);

                const m = pin[floor][i].matrix.clone();
                const elements = m.elements;
                const xVec = new THREE.Vector3(elements[0], elements[1], elements[2]);
                const yVec = new THREE.Vector3(elements[4], elements[5], elements[6]);
                xVec.applyAxisAngle(new THREE.Vector3(0, 1, 0), rotation);
                yVec.applyAxisAngle(new THREE.Vector3(0, 1, 0), rotation);

                const dir = pin[floor][i].dir.clone();

                // マトリクスの算出
                const matrix = new THREE.Matrix4();
                matrix.elements = [
                    xVec.x, xVec.y, xVec.z, elements[3],
                    yVec.x, yVec.y, yVec.z, elements[7],
                    dir.x, dir.y, dir.z, elements[11],
                    pin[floor][i].position.x, pin[floor][i].position.y, pin[floor][i].position.z, elements[15],
                ];
                pin[floor][i].matrix = matrix; 

            }
        }
        return pin;
    }

    /**
     * 自動補正 デフォルト補正
     * */
    public autoCorrection() {
        marietta.webcad.WebCADGlobal.layerGroup.initMe(); // 図面からアイテムを削除

        const manager = new CorrectionManager(this.buildingType);
        for (let floor = 0; floor < this.walls.length; floor++) {
            if (this.pointList[floor].length == 0) continue;
            /** 家の傾き補正 */
            this.pointList[floor] = manager.Correction[0].func(this.pointList[floor], floor, manager.Correction[0]);
            /** 直線化 */
            this.pointList[floor] = manager.Correction[1].func(this.pointList[floor], floor, manager.Correction[1]);
            /** 壁の歪み補正 */
            this.pointList[floor] = manager.Correction[2].func(this.pointList[floor], floor, manager.Correction[2]);
            /** モジュールピッチ補正 */
            if (this.buildingType == BUILDING_TYPE_HOUSE) {
                this.pointList[floor] = manager.Correction[3].func(this.pointList[floor], floor, manager.Correction[3]);
            } else {
                this.pointList[floor] = manager.Correction[13].func(this.pointList[floor], floor, manager.Correction[13]);
            }
            /** 短い壁の削除 */
            this.pointList[floor] = manager.Correction[4].func(this.pointList[floor], floor, manager.Correction[4]);
            /** 小さい部屋の削除 */
            this.pointList[floor] = manager.Correction[5].func(this.pointList[floor], floor, manager.Correction[5]);
            /** 細い部屋の削除 */
            this.pointList[floor] = manager.Correction[12].func(this.pointList[floor], floor, manager.Correction[12]);
            /** 斜めの独立壁の削除 */
            this.pointList[floor] = manager.Correction[10].func(this.pointList[floor], floor, manager.Correction[10]);
            /** グリッドに乗せる補正(戸建て) */
            this.pointList[floor] = manager.Correction[6].func(this.pointList[floor], floor, manager.Correction[6]);
            /** 直線状の点の結合 */
            this.pointList[floor] = manager.Correction[11].func(this.pointList[floor], floor, manager.Correction[11]);    
            /** 外周補正 */
            this.pointList[floor] = manager.Correction[7].func(this.pointList[floor], floor, manager.Correction[7]);
            /** 交わる壁の頂点の追加 */
            this.pointList[floor] = manager.Correction[8].func(this.pointList[floor], floor, manager.Correction[8]);
            /** 重なっている独立壁を削除する */
            this.pointList[floor] = manager.Correction[9].func(this.pointList[floor], floor, manager.Correction[9]);
                        
            // 頂点リストから部屋・壁を２Ｄ描画
            this.DrawFromPointList(this.pointList[floor], floor+1);
        }

        marietta.WebCADGlobal.itemManager.currentItem = null;
    }

    /**
     * 番号によって呼び出す補正関数を変更する
     * */
    public selectCorrection(num: number) {
        marietta.webcad.WebCADGlobal.layerGroup.initMe(); // 図面からアイテムを削除
        
        const manager = new CorrectionManager(this.buildingType);
        for (let floor = 0; floor < this.walls.length; floor++) {

            const index = num - 1; // 配列番号との差分を補正
            const correctionObj: CorrectionType = manager.Correction[index];
            this.pointList[floor] = correctionObj.func(this.pointList[floor], floor, correctionObj);

            // 頂点リストから部屋・壁を２Ｄ描画
            this.DrawFromPointList(this.pointList[floor], floor+1);            
        }
        marietta.WebCADGlobal.itemManager.currentItem = null;
    }

    /**
     * 頂点リストから部屋・壁を２Ｄ描画
     * 
     * @param pointList {PointInfo[]} 頂点リスト
     * @param boxCenter {THREE.Vector3} 家の囲みの中心点
     * @param floor {number} 階
     * */
    private DrawFromPointList(pointList: PointInfo[], floor: number): void {
        const pointData = new CreatePointData(pointList);
        pointData.execute();
        const roomList = pointData.roomList;
        const branchList = pointData.branchList;

        // 2D表示（部屋）
        new AddRoom2D(this.buildingType).execute(roomList, pointList, floor);

        // 2D表示（部屋として取得できなかった壁）
        new AddWall2D(this.buildingType).execute(branchList, pointList, floor);

        // 建具表示
        new AddFitting2D(this.buildingType, this.wallToFittingInfo).execute(pointList, floor);

        // 家具・住設表示
        new AddFurniture2D(this.buildingType, this.furnitureInfo).execute(floor);

        // ピン配置
        new AddPin2D(this.pinInfo).execute(floor);

        /** 必要な更新処理 */
        this.needUpdate();
    }
    
    /** 
     * 必要な更新処理
     * */
    private needUpdate() {
        /** STARTS_LIDAR-998 LiDARデータを取り込んだ直後の挙動にて loadPlan を参考に */
        if (this.buildingType == BUILDING_TYPE_MANSION) {
            marietta.webcad.WebCADGlobal.snapManager.updateSnapElements();
        }
        const itemList = marietta.webcad.WebCADGlobal.layerGroup.getAllItems();
        if (itemList.length > 0) {
            marietta.webcad.WebCADGlobal.webcad2Dmanager.updateWallList();
        }
        marietta.webcad.WebCADGlobal.scrollerManager.controlLayerEnlargementFit();
        marietta.webcad.WebCADGlobal.cadAPIManager.MakeRoomWall();
        marietta.webcad.WebCADGlobal.updateMeasureLine(false, false);
        marietta.webcad.WebCADGlobal.quotationItemList.createData();
        marietta.webcad.WebCADGlobal.webcad2Dmanager.saveToHistory();
    }

    /**
     * offset適用
     * */
    private applyOffset(pointList: PointInfo[], matrix: THREE.Matrix4): PointInfo[] {
        if (matrix == undefined) {
            return pointList.map((info: PointInfo) => {
                return {
                    point: info.point,
                    isExist: true,
                    isDelete: false
                }
            });
        }
        return pointList.map((info: PointInfo) => {
            return {
                point: info.point.applyMatrix4(matrix),
                isExist: true,
                isDelete: false
            }
        });
    }

    /**
     * offset値をセット
     * */
    private setOffset(object: THREE.Group, index: number): void {
        const position = new THREE.Vector3();
        const quaternion = new THREE.Quaternion();
        const scale = new THREE.Vector3();
        const m = new THREE.Matrix4();
        if (this.offset[index] == undefined) return;
        m.elements = this.offset[index];
        const matrix = m.transpose();
        matrix.decompose(position, quaternion, scale);
        object.position.copy(position);
        object.quaternion.copy(quaternion);
        // 旧仕様 
        // if (index==1) this.viewOffsetFirst = matrix;
        // if (index==3) this.viewOffsetSecond = matrix;
        // 新仕様
        if (index==0) this.viewOffsetFirst = matrix;
        if (index==1) this.viewOffsetSecond = matrix;
        if (index==2) this.viewOffsetThird = matrix;
    }

    /**
     * 家の形・offset値をセット
     * */
    private setHouseData() {
        const createMesh = (dimension: number[]) => {
            const width = dimension[0];
            const height = dimension[1];
            const depth = 0.01; // 奥行きは取得できないため決め打ち
            const mesh = CreateObject.createBoxGrometry(width, height, depth, new THREE.Color('#d3d3d3'));
            return mesh;
        }
        
        for (let floor = 0; floor < this.walls.length; floor++) {
            // floor=0 => 1階　floor=1 => 2階　floor=2 => 3階
            for (let i = 0; i < this.walls[floor].length; i++) {
                const wall = this.walls[floor][i];
                const matrix = new THREE.Matrix4();
                matrix.elements = wall.transform;
                const dimension = wall.dimensions;
                // mesh作成
                const mesh = createMesh(dimension);
                mesh.applyMatrix4(matrix);
                if (floor == 0) this.firstFloor.add(mesh);
                if (floor == 1) this.secondFloor.add(mesh);
                if (floor == 2) this.thirdFloor.add(mesh);
            }
            // 旧仕様 
            // if (floor == 0) this.setOffset(this.firstFloor, 1);
            // if (floor == 1) this.setOffset(this.secondFloor, 3);
            // 新仕様
            if (floor == 0) this.setOffset(this.firstFloor, floor);
            if (floor == 1) this.setOffset(this.secondFloor, floor);
            if (floor == 2) this.setOffset(this.thirdFloor, floor);
        }

        for (let floor = 0; floor < this.objects.length; floor++) {            
            for (let i = 0; i < this.objects[floor].length; i++) {
                const object = this.objects[floor][i];
                const matrix = new THREE.Matrix4();
                matrix.elements = object.transform;
                const dimension = object.dimensions;
                const width = dimension[0];
                const height = dimension[1];
                const depth = dimension[2];
                // mesh作成
                const mesh = CreateObject.createBoxGrometry(width, height, depth, new THREE.Color('#deb887'));
                mesh.applyMatrix4(matrix);
                if (floor == 0) this.furnitureFloor1.add(mesh);
            }
        }

    }

    /**
     * 3D表示確認用
     * */
    private create3D() {
        ThreeView.get().wcItemmgr.addLiDARShapes(this.firstFloor);
        ThreeView.get().wcItemmgr.addLiDARShapes(this.furnitureFloor1);
        this.firstFloor.position.setY(1);
        this.furnitureFloor1.position.setY(1);
        // ThreeView.get().wcItemmgr.addLiDARShapes(this.secondFloor);
    }

    /**
     * 回転
     * */
    private rotate(shape: THREE.Group, rad: number): THREE.Group {
        shape.setRotationFromAxisAngle(new THREE.Vector3(0, 1, 0), rad);
        return shape;
    }
    
    /**
     * ピン
     * */
    private cacmeraTest() {
        this.cameraData.forEach((objects: {position: number[], imagePath: string}[] | null) => {
            if (objects == null) return;
            objects.forEach((obj: {position: number[], imagePath: string}) => {

                const matrix = new THREE.Matrix4();
                matrix.elements = obj.position.map(val => val);
                matrix.transpose(); // 転置
                const geometry = new THREE.SphereGeometry(0.1);
                const mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({color: 0xff0000}));
                mesh.applyMatrix4(matrix);
                mesh.position.setY(0);
                ThreeView.get().wcItemmgr.addLiDARShapes(mesh);
                
                // const dir = new THREE.Vector3(matrix.elements[8], matrix.elements[9], matrix.elements[10]).normalize();
                const dir = new THREE.Vector3(0, 0, -1);
                const matrix_ = new THREE.Matrix4();
                matrix_.elements = obj.position.map(val => val);
                matrix_.invert(); // 逆行列
                const origin = new THREE.Vector3(obj.position[3], 0, obj.position[11]);
                dir.applyMatrix4(matrix_);
                const helper = new THREE.ArrowHelper(dir.clone().normalize(), origin, 1);
                ThreeView.get().wcItemmgr.addLiDARShapes(helper);
                
            });
        });
    }

    private getFloorHeight(walls: Wall[]): number {
        if (walls.length == 0) return;

        const height = walls[0].dimensions[1];
        const y = walls[0].transform[13];

        return y - height / 2;
    }
    
    // WEBCADV15-2142 検証：戸建て・マンション モジュールピッチ
    // private bounsLect(roomList: RoomArea, pointList: THREE.Vector3[]) {
    //     for (let i = 0; i < roomList.roomList.length; i++) {
    //         const rooms = roomList.roomList[i];
    //         const min = new THREE.Vector3(Infinity, Infinity, Infinity);
    //         const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
    //         for (let j = 0; j < rooms.length; j++) {
    //             min.min(pointList[rooms[j]]);
    //             max.max(pointList[rooms[j]]);
    //         }
    //         const box = new THREE.Box3(min, max);
    //         console.log(box.getSize(new THREE.Vector3).z);
    //     }
    // }

}